import './BatchAnswerQuestions.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { MouseEvent, ReactElement } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { S_Checkbox } from '../../../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link2 } from '../../../../../../design/links/S_Link2';
import { S_ClampLines } from '../../../../../../design/misc/S_ClampLines';
import { ColumnLayout } from '../../../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { AnnotationStatusBadge } from '../../../../../../modules/common/statusesBadges/annotationStatus/AnnotationStatusBadge';
import { WarningIconBadge } from '../../../../../components/badges/iconBadges/WarningIconBadge';
import { LazyImage } from '../../../../../components/LazyImage';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { Home } from '../../../../../routes/config/Home';
import { ImageSetBadge } from '../../../../annotation/components/ImageSetBadge';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { ImageAnnotationStatus, ImageSetType, IProjectDetailsImage } from '../projectDetailsImages.model';

library.add(faUnlink, faEdit);

interface ThumbnailProps {
  image: IProjectDetailsImage;
  filterId: string;
  projectId: string;
  workspaceId: string;
  authToken: string;
  isChecked: boolean;
  onCheck: (id: string, isBatch: boolean) => void;
  onImageSetChange: (id: string, type: ImageSetType) => Promise<void>;
}

export const ImagesListThumbnail = observer((props: ThumbnailProps): ReactElement => {
  const { image, authToken, workspaceId, projectId, filterId, isChecked, onCheck, onImageSetChange } = props;

  const { t } = useTranslator();
  const permissions = useProjectDetailsPermissions();
  const canUpdateImageSet = permissions.canUpdateImageSet();
  const canBatchAnswerQuestions = permissions.canBatchAnswer();
  const canSelectImages = canBatchAnswerQuestions.allowed;
  const showCheckbox = canBatchAnswerQuestions.allowed;

  const getNavigationUrl = (imageId: string): string => {
    return Home.FreeAccess.View.withParams({ workspaceId, projectId, imageId, filterId });
  };

  const renderImage = (): ReactElement => (
    <LazyLoadComponent threshold={300}>
      <LazyImage
        source={image.url}
        headers={new Headers({ Authorization: `Bearer ${authToken}` })}
      />
    </LazyLoadComponent>
  );

  const renderName = (): ReactElement => (
    <S_ClampLines
      text={image.name}
      lines={2}
    />
  );

  const renderDatasetName = (): ReactElement => {
    const { datasetStatus } = image;
    const showWarning = datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading';

    return (
      <div className='dataset-name-with-icon'>
        {showWarning && (
          <WarningIconBadge
            label=''
            title={t('unpublished')}
          />
        )}
        <S_ClampLines
          text={image.datasetName}
          lines={1}
        />
      </div>
    );
  };

  const onChange = (e: MouseEvent<HTMLSpanElement>, id: string): void => {
    if (canSelectImages && !image.isLocked) {
      onCheck(id, e.nativeEvent.shiftKey);
    }
  };

  const handleImageSetChange = async (value: ImageSetType): Promise<void> => {
    await onImageSetChange(image.id, value);
  };

  const hasClarification = image.status === ImageAnnotationStatus.CLARIFICATION;

  return (
    <div className={`thumb ${hasClarification ? 'clarification' : ''}`}>
      <div className='image-container'>
        <div className='image-wrapper'>
          <S_Link2
            color='text'
            to={getNavigationUrl(image.id)}
          >
            {renderImage()}
          </S_Link2>
        </div>
        <EnableFor hasPermission={canUpdateImageSet}>
          <ImageSetBadge
            set={image.imageSet}
            onChange={handleImageSetChange}
          />
        </EnableFor>
        {showCheckbox && (
          <>
            {canSelectImages && (
              <div
                className='thumb-checkbox'
                title={image.isLocked ? t('someone_is_working') : ''}
              >
                <S_Checkbox
                  checked={isChecked}
                  onClick={(e): void => {
                    onChange(e, image.id);
                  }}
                  value={image.id}
                  id={image.id}
                  disabled={image.isLocked}
                  className={`${isChecked ? 'visible' : ''} ${image.isLocked ? 'locked' : ''}`}
                />
              </div>
            )}
          </>
        )}
      </div>
      <ColumnLayout
        size='sm'
        className='description'
      >
        <div className='file-name'>
          <S_Link2
            color='text'
            title={image.name}
            to={getNavigationUrl(image.id)}
          >
            {renderName()}
          </S_Link2>
        </div>
        <div
          className='dataset-name'
          title={image.datasetName}
        >
          {renderDatasetName()}
        </div>
        <div className='annotation-status'>
          <AnnotationStatusBadge annotationStatus={image.status} />
        </div>
      </ColumnLayout>
    </div>
  );
});
